import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, RefreshCw, Save, ArrowUpRight, Factory, Receipt, Truck, Info,
} from 'lucide-react';
import { financeService } from '../../services/financeService';
import { useAppContext } from '../../context/AppContext';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import { usePeriodDrilldown } from '../../hooks/usePeriodDrilldown';

// =============================================================================
// Finance › Working Capital   (LIVE)
//
// THIS PAGE IS ABOUT PROVENANCE, NOT ASSUMPTIONS.
//
// The obvious build is three boxes — debtor days, creditor days, stock days —
// and it would be a lie in this product, because Mantle already knows the real
// drivers and does not need to be told them:
//
//     INVENTORY          <- Manufacturing.  Production volumes at cost, less
//                           what is sold. A real number, not a ratio.
//     TRADE RECEIVABLES  <- Revenue.        Net revenue billed, collected over
//                           the days each customer contracts to pay in.
//     TRADE PAYABLES     <- Procurement.    Purchases, paid over the days each
//                           supplier contracts to be paid in.
//
// So every component names the module that produces it and links to the page
// where it is actually maintained. The days assumptions are still here and
// still editable, but they are demoted to what they really are: the FALLBACK
// for a counterparty with no terms on record.
//
// THE CHIP THAT MATTERS is "from contracts" versus "from the fallback". A days
// figure taken from a signed payment term and one somebody typed into a box are
// worth completely different amounts in a budget review, and this is the only
// screen in the product that can tell you which is which.
//
// INVENTORY DELIBERATELY DOES NOT USE ITS DAYS FIELD. Where a production plan
// exists the balance is computed from it, and inventory_days is never
// consulted. It stays on the record as a fallback for a site that plans no
// production, and the page says so rather than leaving a live-looking input
// that changes nothing.
// =============================================================================

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-slate-900 text-white hover:bg-slate-700 dark:bg-slate-200 dark:text-slate-900 dark:hover:bg-white disabled:opacity-40 transition-colors';

const fmtR = (n) => {
  const v = Math.round(Number(n) || 0);
  if (v === 0) return '—';
  const s = Math.abs(v).toLocaleString();
  return v < 0 ? `(${s})` : s;
};

const T_COMPONENT = { bgCell: 'rgba(100, 116, 139, 0.13)', baseFontStyle: '600 12px' };
const T_BALANCE = { bgCell: 'rgba(37, 99, 235, 0.13)', baseFontStyle: '700 12px' };
const T_YEAR = { bgCell: 'rgba(100, 116, 139, 0.10)' };

const ICONS = { INVENTORY: Factory, TRADE_DEBTORS: Receipt, TRADE_CREDITORS: Truck };

// What each days_source actually means, said in words. "GROUP_TERMS" on its own
// tells a reader nothing about how much to trust the number.
const SOURCE_LABEL = {
  CUSTOMER_TERMS: 'the customer’s own payment terms',
  GROUP_TERMS: 'their customer group’s terms',
  SUPPLIER_TERMS: 'the supplier’s payment terms',
  FALLBACK: 'the fallback assumption below',
  ASSUMPTION: 'the fallback assumption below',
};
const IS_CONTRACT = (s) => s !== 'FALLBACK' && s !== 'ASSUMPTION';

const EMPTY = { assumptions: [], components: [] };

export default function WorkingCapital() {
  const navigate = useNavigate();
  const { selectedVersion, fyStartMonth } = useAppContext();

  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await financeService.getWorkingCapital();
      const d = res.data || EMPTY;
      setData(d);
      const group = (d.assumptions || []).find((a) => !a.site_code) || {};
      setForm({
        debtor_days: group.debtor_days ?? '',
        creditor_days: group.creditor_days ?? '',
        inventory_days: group.inventory_days ?? '',
        tax_rate: group.tax_rate ?? '',
        tax_payment_lag_periods: group.tax_payment_lag_periods ?? '',
        vat_enabled: group.vat_enabled ?? true,
        vat_payment_lag_periods: group.vat_payment_lag_periods ?? '',
        opening_cash_override: group.opening_cash_override ?? '',
      });
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load working capital.');
      setData(EMPTY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (selectedVersion) load(); }, [load, selectedVersion]);

  const components = useMemo(() => data.components || [], [data.components]);

  const save = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      await financeService.saveWorkingCapital({ site_code: null, ...form });
      setNotice('Saved. Every days-driven figure below has been recomputed.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  // ---- The grid: three components, each as driver / days / balance ----------
  const rows = useMemo(() => {
    const out = [];
    components.forEach((c) => {
      out.push({ kind: 'component', key: c.key, label: c.label, comp: c, values: c.balance });
      out.push({ kind: 'driver', key: `${c.key}-drv`, label: c.driverLabel, values: c.driver });
      if (c.usesDays && c.daysUsed) {
        out.push({ kind: 'days', key: `${c.key}-days`, label: 'Days used', values: c.daysUsed, isDays: true });
      }
      out.push({ kind: 'balance', key: `${c.key}-bal`, label: `${c.label} — closing balance`, values: c.balance });
    });
    return out;
  }, [components]);

  const {
    visibleCols, yearExpanded, expandedQuarters,
    toggleYear, toggleQuarter, expandAll, collapseAll,
  } = usePeriodDrilldown(PERIODS);

  const FIXED = 1;

  const gcols = useMemo(() => {
    const dyn = visibleCols.map((c) => {
      if (c.kind === 'year') return { id: 'YEAR', title: `${yearExpanded ? '▾' : '▸'} Full Year`, width: 132 };
      if (c.kind === 'quarter') return { id: `Q${c.q}`, title: `${expandedQuarters[c.q] ? '▾' : '▸'} Q${c.q}`, width: 112 };
      return { id: `P${c.period}`, title: periodLabel(c.period, fyStartMonth), width: 94 };
    });
    return [{ id: 'label', title: 'Component', width: 300, grow: 1 }, ...dyn];
  }, [visibleCols, yearExpanded, expandedQuarters, fyStartMonth]);

  const colIds = useMemo(() => gcols.map((c) => c.id), [gcols]);

  const getCellContent = useCallback(([col, row]) => {
    const id = colIds[col];
    const r = rows[row];
    if (!r) return gcell.text('', { readonly: true });

    const theme = r.kind === 'component' ? T_COMPONENT
      : r.kind === 'balance' ? T_BALANCE : undefined;

    if (id === 'label') {
      if (r.kind === 'component') {
        return gcell.text(`${r.label.toUpperCase()}  ←  ${r.comp.moduleLabel}`, { readonly: true, theme });
      }
      return gcell.text(`  ${r.label}`, { readonly: true, theme });
    }

    const d = visibleCols[col - FIXED];
    if (!d) return gcell.text('', { readonly: true, theme });
    if (r.kind === 'component') return gcell.text('', { readonly: true, theme });

    // A BALANCE IS A POSITION, NOT A FLOW. Quarter and year columns show the
    // CLOSING figure; summing twelve closing balances would be meaningless.
    // The driver is a flow and IS summed. Days are an average, not either.
    let v;
    if (d.kind === 'month') {
      v = r.values[d.period - 1] || 0;
    } else if (r.kind === 'balance' || r.kind === 'component') {
      v = r.values[d.periods[d.periods.length - 1] - 1] || 0;
    } else if (r.isDays) {
      const live = d.periods.map((p) => r.values[p - 1]).filter((x) => x);
      v = live.length ? live.reduce((a, b) => a + b, 0) / live.length : 0;
    } else {
      v = d.periods.reduce((a, p) => a + (r.values[p - 1] || 0), 0);
    }

    return gcell.number(v, {
      display: r.isDays ? (v ? `${Math.round(v)} d` : '—') : fmtR(v),
      readonly: true,
      theme: theme || (d.kind === 'month' ? undefined : T_YEAR),
    });
  }, [colIds, rows, visibleCols, FIXED]);

  const onHeaderClicked = useCallback((colIndex) => {
    const id = colIds[colIndex];
    if (id === 'YEAR') toggleYear();
    else if (/^Q\d+$/.test(id)) toggleQuarter(Number(id.slice(1)));
  }, [colIds, toggleYear, toggleQuarter]);

  const siteOverrides = useMemo(
    () => (data.assumptions || []).filter((a) => a.site_code),
    [data.assumptions]
  );

  if (!selectedVersion) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">Select a budget version to set working capital.</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <button className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                    onClick={() => navigate('/finance')}>
              <ArrowLeft className="w-3.5 h-3.5" /> Finance
            </button>
            <span className="text-slate-300 dark:text-slate-600">/</span>
            <h1 className="m-0 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 whitespace-nowrap">
              Working Capital
            </h1>
            <span className="text-xs font-semibold text-slate-400">{selectedVersion}</span>
          </div>
          <div className="flex items-center gap-2">
            <button className={btn} onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto space-y-3">
        {error && (
          <div className="rounded-lg border border-red-300 dark:border-red-500/40 bg-red-50 dark:bg-red-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}
        {notice && (
          <div className="rounded-lg border border-emerald-300 dark:border-emerald-500/40 bg-emerald-50 dark:bg-emerald-500/10 px-4 py-2.5">
            <p className="m-0 text-xs font-semibold text-emerald-700 dark:text-emerald-300">{notice}</p>
          </div>
        )}

        {/* WHERE EACH COMPONENT COMES FROM. Three cards, above the numbers,
            because the provenance is the answer to the first question anybody
            asks of a working capital figure: "where did that come from?" */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          {components.map((c) => {
            const Icon = ICONS[c.key] || Info;
            const total = c.daysSources.reduce((s, x) => s + x.n, 0);
            const contract = c.daysSources.filter((x) => IS_CONTRACT(x.days_source))
              .reduce((s, x) => s + x.n, 0);
            return (
              <div key={c.key} className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-slate-400 shrink-0" />
                    <h2 className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">{c.label}</h2>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300 whitespace-nowrap">
                    {c.moduleLabel}
                  </span>
                </div>

                <p className="m-0 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
                  Driven by <strong className="text-slate-700 dark:text-slate-200">{c.driverLabel.toLowerCase()}</strong>
                  {' '}— {fmtR(c.driver.reduce((a, b) => a + b, 0))} for the year.
                </p>

                {c.usesDays ? (
                  total ? (
                    <p className="m-0 mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                      <strong className={contract === total
                        ? 'text-emerald-700 dark:text-emerald-400'
                        : 'text-amber-700 dark:text-amber-400'}>
                        {contract} of {total}
                      </strong>{' '}
                      resolve from a contract; the rest use the fallback below.
                      <span className="block mt-0.5 text-slate-400">
                        {c.daysSources.map((s) => `${SOURCE_LABEL[s.days_source] || s.days_source}: ${s.n} at ${s.avg_days}d`).join(' · ')}
                      </span>
                    </p>
                  ) : (
                    <p className="m-0 mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                      Nothing has payment terms on record — every day of this balance is the fallback below.
                    </p>
                  )
                ) : (
                  <p className="m-0 mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
                    <strong className="text-emerald-700 dark:text-emerald-400">Computed, not assumed.</strong>{' '}
                    Opening plus produced less sold, at cost — the stock-days ratio is not used at all where a
                    production plan exists.
                  </p>
                )}

                <button
                  className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700 hover:underline dark:text-sky-400"
                  onClick={() => navigate(c.route)}
                >
                  Maintain in {c.moduleLabel} <ArrowUpRight className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>

        {/* The numbers. */}
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex flex-col overflow-hidden">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              Nothing to show for {selectedVersion} yet.
            </p>
          ) : (
            <MantleDataGrid
              gridId="finance-working-capital"
              fitHeight
              fitHeightPadding={6}
              columns={gcols}
              rowCount={rows.length}
              getCellContent={getCellContent}
              onHeaderClicked={onHeaderClicked}
              freezeColumns={1}
              hint="Drivers are flows and are summed across periods. Balances are positions — a quarter column shows the CLOSING balance, never a total of twelve."
              toolbar={
                <>
                  <span className="text-[11px] text-slate-500 dark:text-slate-400">
                    read-only — every figure comes from the module named on its card
                  </span>
                  <button type="button" onClick={yearExpanded ? collapseAll : expandAll} className={btn}>
                    {yearExpanded ? 'Collapse periods' : 'Expand periods'}
                  </button>
                </>
              }
            />
          )}
        </div>

        {/* The only editable thing on the page, and it is explicitly the
            fallback rather than the driver. */}
        {form && (
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <h2 className="m-0 text-sm font-semibold text-slate-800 dark:text-slate-100">
                  Group assumptions
                </h2>
                <p className="m-0 mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                  Used where a counterparty has no terms on record, plus the tax and VAT settings, which nothing
                  else in Mantle holds. Leave a field blank for &ldquo;not set&rdquo; &mdash; that is not the same as zero.
                </p>
              </div>
              <button className={btnPrimary} onClick={save} disabled={saving}>
                <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save assumptions'}
              </button>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Debtor days" hint="fallback only" value={form.debtor_days}
                     onChange={(v) => setForm({ ...form, debtor_days: v })} suffix="days" />
              <Field label="Creditor days" hint="fallback only" value={form.creditor_days}
                     onChange={(v) => setForm({ ...form, creditor_days: v })} suffix="days" />
              <Field label="Inventory days" hint="unused where production is planned" value={form.inventory_days}
                     onChange={(v) => setForm({ ...form, inventory_days: v })} suffix="days" muted />
              <Field label="Tax rate" value={form.tax_rate}
                     onChange={(v) => setForm({ ...form, tax_rate: v })} suffix="%" />
              <Field label="Tax paid after" value={form.tax_payment_lag_periods}
                     onChange={(v) => setForm({ ...form, tax_payment_lag_periods: v })} suffix="periods" />
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">VAT</label>
                <select
                  className="w-full px-2 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100"
                  value={form.vat_enabled ? 'yes' : 'no'}
                  onChange={(e) => setForm({ ...form, vat_enabled: e.target.value === 'yes' })}
                >
                  <option value="yes">Modelled</option>
                  <option value="no">Excluded</option>
                </select>
              </div>
              <Field label="VAT paid after" value={form.vat_payment_lag_periods}
                     onChange={(v) => setForm({ ...form, vat_payment_lag_periods: v })} suffix="periods" />
              <Field label="Opening cash override" hint="blank = use act_gl" value={form.opening_cash_override}
                     onChange={(v) => setForm({ ...form, opening_cash_override: v })} />
            </div>

            {siteOverrides.length > 0 && (
              <div className="px-4 pb-4">
                <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Site overrides
                </p>
                <div className="flex flex-wrap gap-2">
                  {siteOverrides.map((s) => (
                    <span key={s.site_code}
                          className="inline-flex items-baseline gap-1.5 px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700 text-[11px] text-slate-600 dark:text-slate-300">
                      <strong className="text-slate-800 dark:text-slate-100">{s.site_code}</strong>
                      {s.debtor_days !== null && <>debtors {s.debtor_days}d</>}
                      {s.creditor_days !== null && <>· creditors {s.creditor_days}d</>}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <p className="m-0 text-[11px] text-slate-400 max-w-4xl">
          Working capital is not entered here &mdash; it is <strong>derived from the modules that own it</strong>.
          Inventory follows the production plan, receivables follow budgeted revenue and the terms each customer
          contracts to, payables follow purchases and supplier terms. What this page captures is only what no
          module owns: the fallback days for a counterparty with no terms on record, and the tax and VAT settings.
        </p>
      </div>
    </div>
  );
}

// Blank stays blank. An input that turns an empty field into 0 on blur quietly
// changes "nobody has said" into "every customer pays cash".
const Field = ({ label, hint, value, onChange, suffix, muted }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">
      {label}{hint && <span className="ml-1 font-medium normal-case tracking-normal text-slate-400">· {hint}</span>}
    </label>
    <div className="relative">
      <input
        type="number"
        className={`w-full px-2 py-1.5 text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 ${muted ? 'opacity-60' : ''}`}
        value={value === null || value === undefined ? '' : value}
        placeholder="not set"
        onChange={(e) => onChange(e.target.value)}
      />
      {suffix && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-slate-400 pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  </div>
);
